import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { useCreatePayment, useUpdatePayment } from "@/api/payments";
import PropTypes from "prop-types";
import { useState } from "react";
import { PaymentReceiptModal } from "@/components/payment/PaymentReceiptModal";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { currencies } from "@/lib/constants";
import { useQuery } from "@tanstack/react-query";
import axios from "@/lib/axios";

const formSchema = z.object({
  name: z.string().min(2, { message: "Name is required" }),
  place: z.string().min(2, { message: "Place is required" }),
  phone: z
    .string()
    .regex(/^[+]?\d{1,15}$/, {
      message: "Please enter a valid phone number (maximum 15 digits)",
    })
    .min(1, { message: "Phone number is required" }),
  chapter: z.string().nullable(),
  amount: z.string().transform((val) => parseInt(val, 10)),
  currency: z.string().min(1, { message: "Currency is required" }),
  careOf: z
    .object({
      name: z.string().optional(),
      phone: z
        .string()
        .regex(/^[+]?\d{1,15}$/, {
          message: "Please enter a valid phone number (maximum 15 digits)",
        })
        .optional()
        .or(z.literal("")),
    })
    .optional(),
  verified: z.boolean().default(false),
});

export function AddPaymentForm({ onSuccess, initialData = null }) {
  const { mutate: createPayment, isPending: isCreating } = useCreatePayment();
  const { mutate: updatePayment, isPending: isUpdating } = useUpdatePayment();
  const [completedPayment, setCompletedPayment] = useState(null);

  // Add chapters query with sorting
  const { data: chapters, isLoading: isLoadingChapters } = useQuery({
    queryKey: ["chapters"],
    queryFn: async () => {
      const response = await axios.get("/chapters");
      return response.data.sort((a, b) =>
        a.chapterName.localeCompare(b.chapterName),
      );
    },
  });

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: initialData?.name || "",
      place: initialData?.place || "",
      phone: initialData?.phone || "",
      chapter: initialData?.chapter?._id || "none",
      amount: initialData?.amount?.toString() || "",
      currency: initialData?.currency || "INR",
      careOf: {
        name: initialData?.careOf?.name || "",
        phone: initialData?.careOf?.phone || "",
      },
      verified: initialData?.verified || true,
    },
  });

  function onSubmit(values) {
    const submissionValues = {
      ...values,
      chapter: values.chapter === "none" ? null : values.chapter,
    };

    if (initialData) {
      updatePayment(
        { id: initialData._id, ...submissionValues },
        {
          onSuccess: (data) => {
            form.reset();
            setCompletedPayment(data);
            onSuccess?.();
          },
        },
      );
    } else {
      createPayment(submissionValues, {
        onSuccess: (data) => {
          form.reset();
          setCompletedPayment(data);
          onSuccess?.();
        },
      });
    }
  }

  return (
    <div className="max-h-[calc(100vh-200px)] overflow-y-auto px-1">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
          {/* Basic Details Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-medium text-muted-foreground">
                Basic Details
              </h3>
              <div className="h-px flex-1 bg-border" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter name"
                        {...field}
                        className="h-11 sm:h-8"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="place"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel>Place</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter place"
                        {...field}
                        className="h-11 sm:h-8"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          {/* Payment Details Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-medium text-muted-foreground">
                Payment Details
              </h3>
              <div className="h-px flex-1 bg-border" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel>Phone</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter phone number"
                        {...field}
                        className="h-11 sm:h-8"
                        inputMode="numeric"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="chapter"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel>Chapter (Optional)</FormLabel>
                    <Select
                      disabled={isLoadingChapters}
                      onValueChange={field.onChange}
                      value={field.value || "none"}
                    >
                      <FormControl>
                        <SelectTrigger className="h-11 sm:h-8">
                          <SelectValue placeholder="Select chapter" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        {chapters?.map((chapter) => (
                          <SelectItem key={chapter._id} value={chapter._id}>
                            {chapter.chapterName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-3 sm:col-span-2">
                <FormField
                  control={form.control}
                  name="amount"
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <FormLabel>Amount</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          placeholder="Enter amount"
                          {...field}
                          className="h-11 sm:h-8"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="currency"
                  render={({ field }) => (
                    <FormItem className="space-y-2">
                      <FormLabel>Currency</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger className="h-11 sm:h-8">
                            <SelectValue placeholder="Select" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {Object.entries(currencies).map(
                            ([code, currency]) => (
                              <SelectItem key={code} value={code}>
                                {currency.symbol} {code}
                              </SelectItem>
                            ),
                          )}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </div>

          {/* Verification Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-medium text-muted-foreground">
                Verification
              </h3>
              <div className="h-px flex-1 bg-border" />
            </div>

            <FormField
              control={form.control}
              name="verified"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>Verify Payment</FormLabel>
                    <p className="text-sm text-muted-foreground">
                      Mark this payment as verified upon creation
                    </p>
                  </div>
                </FormItem>
              )}
            />
          </div>

          {/* Care Of Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <h3 className="text-sm font-medium text-muted-foreground">
                Care Of (Optional)
              </h3>
              <div className="h-px flex-1 bg-border" />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="careOf.name"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel>Care of Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter care of name"
                        {...field}
                        className="h-11 sm:h-8"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="careOf.phone"
                render={({ field }) => (
                  <FormItem className="space-y-2">
                    <FormLabel>Care of Phone</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="Enter care of phone"
                        {...field}
                        className="h-11 sm:h-8"
                        inputMode="numeric"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </div>

          {/* Submit Button */}
          <div className="sticky bottom-0 pt-4 pb-2 bg-background">
            <Button
              type="submit"
              className="w-full h-12 sm:h-8 text-base sm:text-sm rounded-lg"
              disabled={isCreating || isUpdating}
            >
              {isCreating || isUpdating ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  {initialData ? "Updating Payment..." : "Adding Payment..."}
                </>
              ) : initialData ? (
                "Update Payment"
              ) : (
                "Add Payment"
              )}
            </Button>
          </div>
        </form>
      </Form>

      <PaymentReceiptModal
        isOpen={!!completedPayment}
        onClose={() => setCompletedPayment(null)}
        payment={completedPayment}
      />
    </div>
  );
}

AddPaymentForm.propTypes = {
  onSuccess: PropTypes.func,
  initialData: PropTypes.object,
};
